/**
 * Composable for Cloudinary URL transformations
 * @returns {Object} Transformation methods
 */
export function useCloudinaryTransform() {
  /**
   * Add transformations to a Cloudinary URL
   * @param {string} url - Original Cloudinary URL
   * @param {string} transformations - Transformation string (e.g., 'w_228,h_100,c_pad,q_auto,f_auto')
   * @returns {string} Transformed Cloudinary URL
   */
  const addTransformations = (url, transformations) => {
    if (!url) return ''

    // Check if it's a Cloudinary URL
    if (!url.includes('res.cloudinary.com')) {
      return url
    }

    // Pattern: https://res.cloudinary.com/{cloud_name}/image/upload/{version}/{path}
    // We need to inject transformations after 'upload/'
    const uploadPattern = /\/upload\//

    if (!uploadPattern.test(url)) {
      console.warn('URL does not contain /upload/ pattern:', url)
      return url
    }

    // Insert transformations after '/upload/'
    const transformedUrl = url.replace(/\/upload\//, `/upload/${transformations}/`)

    return transformedUrl
  }

  /**
   * Transform logo URL with standard logo transformations
   * @param {string} url - Original Cloudinary logo URL
   * @param {Object} options - Transformation options
   * @param {boolean} options.addWhiteBackground - Add white background (for dark mode fallback)
   * @returns {string} Transformed logo URL
   */
  const transformLogo = (url, { addWhiteBackground = false } = {}) => {
    // Standard logo transformations: width 228, height 50, pad, auto quality & format
    const baseTransform = 'w_228,h_50,c_pad,q_auto,f_auto'
    const transformations = addWhiteBackground
      ? `${baseTransform}/b_rgb:ffffff`
      : baseTransform

    return addTransformations(url, transformations)
  }

  /**
   * Transform image URL with custom dimensions
   * @param {string} url - Original Cloudinary URL
   * @param {Object} options - Transformation options
   * @param {number} options.width - Image width
   * @param {number} options.height - Image height
   * @param {string} options.crop - Crop mode (default: 'pad')
   * @param {string} options.quality - Quality setting (default: 'auto')
   * @param {string} options.format - Format setting (default: 'auto')
   * @returns {string} Transformed URL
   */
  const transformImage = (url, { width, height, crop = 'pad', quality = 'auto', format = 'auto' } = {}) => {
    const transformations = [
      width && `w_${width}`,
      height && `h_${height}`,
      crop && `c_${crop}`,
      quality && `q_${quality}`,
      format && `f_${format}`
    ].filter(Boolean).join(',')

    return addTransformations(url, transformations)
  }

  return {
    addTransformations,
    transformLogo,
    transformImage
  }
}
